import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  List,
  ListItem,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Divider,
  Button,
  TextField,
  InputAdornment
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faEllipsisV,
  faEdit,
  faCloudUploadAlt,
  faCopy,
  faChartBar,
  faCalendarAlt,
  faUser
} from '@fortawesome/free-solid-svg-icons';

export interface VersionItem {
  id: string;
  timestamp: string;
  date: string;
  modifier: string;
  isCurrent?: boolean;
  title?: string;
}

interface VersionHistoryProps {
  open: boolean;
  onClose: () => void;
  versions: VersionItem[];
  onVersionSelect?: (version: VersionItem) => void;
  onRename?: (version: VersionItem, newName: string) => void;
  onRestore?: (version: VersionItem) => void;
  onCopy?: (version: VersionItem) => void;
  onCompare?: (version1: VersionItem, version2: VersionItem) => void;
  title?: string;
}

const VersionHistory: React.FC<VersionHistoryProps> = ({
  open,
  onClose,
  versions,
  onVersionSelect,
  onRename,
  onRestore,
  onCopy,
  onCompare,
  title = 'History'
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedVersion, setSelectedVersion] = useState<VersionItem | null>(null);
  const [renameMode, setRenameMode] = useState(false);
  const [renameValue, setRenameValue] = useState('');

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, version: VersionItem) => {
    setAnchorEl(event.currentTarget);
    setSelectedVersion(version);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedVersion(null);
    setRenameMode(false);
    setRenameValue('');
  };

  const handleRename = () => {
    setRenameMode(true);
    setRenameValue(selectedVersion?.title || '');
  };

  const handleRenameSubmit = () => {
    if (selectedVersion && onRename && renameValue.trim()) {
      onRename(selectedVersion, renameValue.trim());
    }
    handleMenuClose();
  };

  const handleRestore = () => {
    if (selectedVersion && onRestore) {
      onRestore(selectedVersion);
    }
    handleMenuClose();
  };

  const handleCopy = () => {
    if (selectedVersion && onCopy) {
      onCopy(selectedVersion);
    }
    handleMenuClose();
  };

  const handleCompare = () => {
    if (selectedVersion && onCompare) {
      // For compare, you might want to implement a selection mechanism
      // This is a simplified version that compares with the current version
      const currentVersion = versions.find(v => v.isCurrent);
      if (currentVersion) {
        onCompare(selectedVersion, currentVersion);
      }
    }
    handleMenuClose();
  };

  const formatTime = (timestamp: string) => {
    try {
      // Handle MongoDB date format with $date wrapper
      let dateString = timestamp;
      if (typeof timestamp === 'string' && timestamp.includes('$date')) {
        // Parse MongoDB date format
        const parsed = JSON.parse(timestamp);
        dateString = parsed.$date;
      }
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return date.toLocaleString('en-US', {
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const capitalizeFirstLetter = (str: string | undefined) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: 360,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box
            sx={{
              p: 3,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {title}
            </Typography>
            <IconButton onClick={onClose} size="small">
              <FontAwesomeIcon icon={faTimes} />
            </IconButton>
          </Box>

          {/* Filter/Sort */}
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Last modified"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FontAwesomeIcon icon={faCalendarAlt} style={{ color: '#666', fontSize: 16 }} />
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'grey.50'
                }
              }}
            />
          </Box>

          {/* Version List */}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <List sx={{ py: 1 }}>
              {versions.map((version, index) => (
                <React.Fragment key={version.id}>
                  <ListItem
                    sx={{
                      py: 2,
                      px: 3,
                      cursor: 'pointer',
                      position: 'relative',
                      '&:hover': {
                        backgroundColor: 'grey.50'
                      }
                    }}
                    onClick={() => onVersionSelect?.(version)}
                  >
                    {/* Timeline indicator */}
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 24,
                        top: 0,
                        bottom: 0,
                        width: 2,
                        backgroundColor: 'grey.300',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 16,
                          left: -4,
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: version.isCurrent ? 'primary.main' : 'grey.400',
                          border: '2px solid white',
                          boxShadow: '0 0 0 1px rgba(0,0,0,0.1)',
                          zIndex: 1
                        }
                      }}
                    />

                    <Box sx={{ ml: 4, flex: 1 }}>
                      {/* Version title */}
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                        {capitalizeFirstLetter(version.title)}
                      </Typography>

                      {/* Primary timestamp and current badge */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 0.5
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                          {formatTime(version.timestamp)}
                        </Typography>
                        {version.isCurrent && (
                          <Chip
                            label="Current"
                            size="small"
                            sx={{
                              backgroundColor: 'success.light',
                              color: 'success.contrastText',
                              fontSize: '0.75rem',
                              height: 20
                            }}
                          />
                        )}
                      </Box>

                      {/* Modifier */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <FontAwesomeIcon icon={faUser} style={{ fontSize: 12, color: '#666' }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          By {version.modifier}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Options menu */}
                    <ListItemSecondaryAction>
                      <IconButton
                        size="small"
                        onClick={e => handleMenuOpen(e, version)}
                        sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
                      >
                        <FontAwesomeIcon icon={faEllipsisV} />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < versions.length - 1 && <Divider sx={{ mx: 3 }} />}
                </React.Fragment>
              ))}
            </List>
          </Box>
        </Box>
      </Drawer>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            minWidth: 200,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            borderRadius: 2
          }
        }}
      >
        {renameMode ? (
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              size="small"
              value={renameValue}
              onChange={e => setRenameValue(e.target.value)}
              placeholder="Enter new name"
              autoFocus
              onKeyPress={e => {
                if (e.key === 'Enter') {
                  handleRenameSubmit();
                }
              }}
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button size="small" onClick={handleRenameSubmit}>
                Save
              </Button>
              <Button size="small" onClick={handleMenuClose}>
                Cancel
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            <MenuItem onClick={handleRename} sx={{ py: 1.5 }}>
              <FontAwesomeIcon icon={faEdit} style={{ marginRight: 12, fontSize: 16 }} />
              <Typography variant="body2">Rename</Typography>
            </MenuItem>
            <MenuItem onClick={handleRestore} sx={{ py: 1.5 }}>
              <FontAwesomeIcon icon={faCloudUploadAlt} style={{ marginRight: 12, fontSize: 16 }} />
              <Typography variant="body2">Restore this version</Typography>
            </MenuItem>
            <MenuItem onClick={handleCopy} sx={{ py: 1.5 }}>
              <FontAwesomeIcon icon={faCopy} style={{ marginRight: 12, fontSize: 16 }} />
              <Typography variant="body2">Make a copy</Typography>
            </MenuItem>
            <MenuItem onClick={handleCompare} sx={{ py: 1.5 }}>
              <FontAwesomeIcon icon={faChartBar} style={{ marginRight: 12, fontSize: 16 }} />
              <Typography variant="body2">Compare versions</Typography>
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
};

export default VersionHistory;
